import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { ArrowRight, CheckCircle2, Shield, Wallet2 } from "lucide-react";
import { Link } from "react-router";

const fadeIn = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.5 },
};

const stagger = {
  animate: {
    transition: {
      staggerChildren: 0.1,
    },
  },
};

export default function LandingPage() {
  return (
    <>
      <header className="px-4 p-4 lg:px-6 h-14 flex items-center">
        <Link className="flex items-center justify-center" to="/">
          <motion.span
            className="font-bold text-2xl"
            initial={{ opacity: 0, scale: 0.5 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5 }}
          >
            nett<span className="text-primary">pay</span>
          </motion.span>
        </Link>
        <nav className="ml-auto flex gap-4 sm:gap-6">
          <Link
            className="text-sm font-medium hover:underline underline-offset-4"
            to="#features"
          >
            Features
          </Link>
          <Link
            className="text-sm font-medium hover:underline underline-offset-4"
            to="#pricing"
          >
            Pricing
          </Link>
          <Link
            className="text-sm font-medium hover:underline underline-offset-4"
            to="#about"
          >
            About
          </Link>
        </nav>
        <div className="ml-4 flex items-center gap-2">
          <Link to="/auth/login">
            <Button variant="ghost">Login</Button>
          </Link>
          <Link to="/auth/register">
            <Button>Get Started</Button>
          </Link>
        </div>
      </header>
      <main className="flex-1 overflow-y-auto min-h-screen">
        <section className="w-full py-12 md:py-24 lg:py-32 xl:py-48 relative overflow-y-auto">
          <motion.div
            className="absolute inset-0 z-0"
            animate={{
              backgroundPosition: ["0% 0%", "100% 100%"],
            }}
            transition={{
              duration: 20,
              ease: "linear",
              repeat: Infinity,
              repeatType: "reverse",
            }}
            style={{
              backgroundImage:
                "linear-gradient(to right top, #6ee7b7, #3b82f6)",
              backgroundSize: "400% 400%",
            }}
          />
          <div className="container px-4 md:px-6 relative z-10">
            <motion.div
              className="flex flex-col items-center space-y-4 text-center"
              variants={stagger}
              initial="initial"
              animate="animate"
            >
              <motion.div className="space-y-2" variants={fadeIn}>
                <h1 className="text-3xl font-bold tracking-tighter sm:text-4xl md:text-5xl lg:text-6xl/none text-white">
                  The Future of Financial Transactions
                </h1>
                <p className="mx-auto max-w-[700px] text-gray-200 md:text-xl">
                  Streamline your payments, enhance financial services, and
                  scale your business with our powerful multi-tenant transaction
                  system.
                </p>
              </motion.div>
              <motion.div className="space-x-4" variants={fadeIn}>
                <Link to="/auth/register">
                  <Button className="px-8" size="lg">
                    Get Started
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </Link>
                <Link to="/contact">
                  <Button variant="outline" className="px-8" size="lg">
                    Contact Sales
                  </Button>
                </Link>
              </motion.div>
            </motion.div>
          </div>
        </section>
        <section
          className="flex justify-center w-full py-12 md:py-24 lg:py-32 bg-gray-50 dark:bg-gray-900"
          id="features"
        >
          <div className="container px-4 md:px-6">
            <motion.div
              className="grid gap-10 px-10 md:gap-16 lg:grid-cols-3"
              variants={stagger}
              initial="initial"
              whileInView="animate"
              viewport={{ once: true }}
            >
              <motion.div className="space-y-4" variants={fadeIn}>
                <div className="inline-block rounded-lg bg-primary/10 p-3">
                  <Wallet2 className="h-6 w-6 text-primary" />
                </div>
                <h3 className="text-xl font-bold">Multi-Tenant Support</h3>
                <p className="text-gray-500 dark:text-gray-400">
                  Empower multiple companies to operate independently, managing
                  their own services, customers, and transactions.
                </p>
              </motion.div>
              <motion.div className="space-y-4" variants={fadeIn}>
                <div className="inline-block rounded-lg bg-primary/10 p-3">
                  <Shield className="h-6 w-6 text-primary" />
                </div>
                <h3 className="text-xl font-bold">Secure Transactions</h3>
                <p className="text-gray-500 dark:text-gray-400">
                  Advanced security measures with role-based access control and
                  robust transaction monitoring.
                </p>
              </motion.div>
              <motion.div className="space-y-4" variants={fadeIn}>
                <div className="inline-block rounded-lg bg-primary/10 p-3">
                  <CheckCircle2 className="h-6 w-6 text-primary" />
                </div>
                <h3 className="text-xl font-bold">Flexible Integration</h3>
                <p className="text-gray-500 dark:text-gray-400">
                  Seamlessly integrate with third-party wallets and services
                  through our standardized protocols.
                </p>
              </motion.div>
            </motion.div>
          </div>
        </section>
        <section className="w-full py-12 md:py-24 lg:py-32">
          <div className="container px-4 md:px-6">
            <motion.div
              className="flex flex-col items-center justify-center space-y-4 text-center"
              variants={stagger}
              initial="initial"
              whileInView="animate"
              viewport={{ once: true }}
            >
              <motion.div className="space-y-2" variants={fadeIn}>
                <h2 className="text-3xl font-bold tracking-tighter md:text-4xl">
                  Start Your Journey Today
                </h2>
                <p className="mx-auto max-w-[600px] text-gray-500 md:text-xl dark:text-gray-400">
                  Join thousands of businesses already using Nettpay to power
                  their financial operations.
                </p>
              </motion.div>
              <motion.div
                className="w-full max-w-sm space-y-2"
                variants={fadeIn}
              >
                <Link to="/auth/register" className="w-full">
                  <Button className="w-full" size="lg">
                    Get Started
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </Link>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  No credit card required. Start your free trial today.
                </p>
              </motion.div>
            </motion.div>
          </div>
        </section>
      </main>
    </>
  );
}
